import { artworkCmd } from './apiClient';

type Presigned = { uploadUrl: string; storageRef: string; storagePath: string; headers?: Record<string, string> };

/** The picture Taste reads is drawn at most this long on its long side. */
export const TASTE_PICTURE_PX = 2048;
/** A PNG the person exported is reused as is up to this size; a bigger one is drawn again smaller. */
export const TASTE_REUSE_BYTES = 8 * 1024 * 1024;

/**
 * Taste (Atlas) learns from what people ship, and an export is the strongest "keep" there is.
 * PNG and SVG exports are drawn here in the browser, so unlike a cloud export nothing was
 * stored for Atlas to read: this keeps ONE PNG of what was exported (the export itself, or its
 * first board drawn as a picture) and records it with artwork-back, which tells Atlas with the
 * file's brand and project. Fire and forget: it never throws and nothing waits on it.
 */
export async function keepExportForTaste(documentId: string, format: 'png' | 'svg', picture: Uint8Array | null, files: number): Promise<void> {
  if (!documentId || !picture?.byteLength) return;
  try {
    const presigned = await artworkCmd<Presigned>('artwork.export.local.presign', { documentId, sizeBytes: picture.byteLength });
    // The presign's headers as they are, and a content type only when they carry none:
    // spreading them over our own sent it twice, and storage answered 403.
    const headers = new Headers(presigned.headers ?? {});
    if (!headers.has('content-type')) headers.set('content-type', 'image/png');
    const put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers,
      body: new Blob([picture as BlobPart], { type: 'image/png' }),
    });
    if (!put.ok) return;
    await artworkCmd('artwork.export.local.record', {
      documentId,
      format,
      storageRef: presigned.storageRef,
      storagePath: presigned.storagePath,
      sizeBytes: picture.byteLength,
      files,
    });
  } catch {
    // Taste is never in the way of an export.
  }
}
